import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@hooks";
import { MainMenu, UserMenu } from "@components/molecules";
import { CloseOutlined, MenuOutlined } from "@ant-design/icons";
import { BreadcrumbNavigation } from "@components/organisms";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button, Skeleton, Spin } from "antd";

export const PrivateTemplate = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showFooter, setShowFooter] = useState(false);
  const { profile: { data: profile, isLoading } } = useAuth();

  useEffect(() => {
    if (!isCollapsed) {
      const timer = setTimeout(() => setShowFooter(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowFooter(false);
    }
  }, [isCollapsed]);

  if (isLoading) {
    return <Skeleton loading className="w-full min-h-screen" />;
  }

  if (!isLoading && !profile) {
    return <Navigate to="/login" />;
  }

  return (
    <Spin spinning={isLoading}>
      <div className="flex w-full h-screen">
        {/* Sidebar Navigation */}
        <nav
          className={`fixed md:relative flex flex-col transition-all duration-300 ease-in-out z-50 h-screen overflow-y-hidden bg-charcoal
          ${menuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
          ${isCollapsed ? "w-24" : "w-72"}`}
        >
          {/* Collapse Button */}
          {!menuOpen && (
            <Button
              type="text"
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={`absolute top-5 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-rock-grey hover:!bg-primary text-white hover:!text-charcoal
              ${isCollapsed ? "left-20" : "left-68"}`}
              icon={isCollapsed ? <CaretRight size={20} /> : <CaretLeft size={20} />}
            />
          )}

          {/* Logo and Menu */}
          <div className="flex-1">
            <div className={`flex items-center justify-center ${isCollapsed ? "p-2" : "p-4"}`}>
              <img
                src={"/assets/logos/ruppert-logo.svg"}
                alt="Logo"
                className={`${isCollapsed ? "w-12" : "w-24"} h-auto transition-all`}
              />
            </div>
            <div className="p-4">
              <MainMenu onMenuItemClick={() => setMenuOpen(false)} collapsed={isCollapsed} />
            </div>
          </div>

          {/* Footer */}
          {!isCollapsed && (
            <div
              className={`p-6 mt-auto transition-opacity duration-300 ${
                showFooter ? "opacity-100" : "opacity-0"
              }`}
            >
              <span className="text-secondary text-xs block uppercase font-light mb-2">
                Powered by
              </span>
              <img src="/logo-horizontal.svg" alt="Estabrook" className="w-auto h-4" />
            </div>
          )}
        </nav>

        {/* Overlay for mobile menu */}
        {menuOpen && <div className="fixed inset-0 bg-black bg-opacity-60 md:hidden z-40" onClick={() => setMenuOpen(false)} />}

        {/* Main Content */}
        <main className="flex-1 flex flex-col overflow-hidden bg-white text-charcoal">
          <div className="h-16 bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="md:hidden text-charcoal z-50 p-2"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? <CloseOutlined /> : <MenuOutlined />}
              </button>
              <div className="hidden md:block">
                <BreadcrumbNavigation />
              </div>
            </div>
            <UserMenu profile={profile} />
          </div>
          <div className="flex-1 overflow-auto p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </Spin>
  );
};